/** @format */
import { Component, EventEmitter, Inject, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FudisDialogService } from '@funidata/ngx-fudis';
import { TranslocoService } from '@ngneat/transloco';
import { ValidatablePlan } from 'common-typescript';
import {
    CourseUnit,
    CustomCourseUnitAttainment,
    CustomModuleContentApplication,
    CustomStudyDraft,
    Module,
    ModuleContentWorkflow,
    ModuleContentWorkflowDecision,
    OtmId,
    RequiredModuleContentApplication,
    StudyRight,
    Workflow,
    WorkflowDecision,
    WorkflowDecisionWithRecommendation,
    WorkflowTypeEnumValue,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { EMPTY, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import {
    AlertsService,
    AppErrorHandler,
    confirmDialogFdOpener,
    ConfirmDialogFdValues,
    PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
    PlanData,
    PlanManager,
    PlanStateObject,
    WorkflowEntityService,
} from 'sis-components';
import { StudentPlanActionsService } from '../../common/service/student-plan-actions.service';

export function moduleContentWorkflowDialogOpener(): (
    data: ModuleContentWorkflowDialogValues,
) => MatDialogRef<ModuleContentWorkflowDialogComponent, Workflow> {
    const dialogService = inject(FudisDialogService);
    return (data: ModuleContentWorkflowDialogValues) => dialogService.open(ModuleContentWorkflowDialogComponent, { data });
}

export interface ModuleContentWorkflowDialogValues {
    approvalRequestType: 'CUSTOM' | 'REQUIRED';
    module: Module;
    validatablePlan: ValidatablePlan;
    matchingStudyRight: StudyRight;
    planData?: PlanData;
    planStateObject?: PlanStateObject;
}

@Component({
    selector: 'app-module-content-workflow-dialog',
    templateUrl: './module-content-workflow-dialog.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    providers: [
        PlanManager,
        {
            provide: PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
            useClass: StudentPlanActionsService,
        },
    ],
})
export class ModuleContentWorkflowDialogComponent implements OnInit {
    approvalRequestType: string;
    parentModule: Module;
    validatablePlan: ValidatablePlan;
    workflow: ModuleContentWorkflow;
    planData: PlanData;
    planStateObject: PlanStateObject;
    newestModuleContentApplication: CustomModuleContentApplication | RequiredModuleContentApplication;
    workflowDecision: WorkflowDecisionWithRecommendation;

    readonly confirmDialogFdOpener = confirmDialogFdOpener();

    private readonly dialogService = inject(FudisDialogService);
    showApplicationCancellationDisabledAlert: boolean = false;
    showApplicationRevocationDisabledAlert: boolean = false;

    constructor(
        private readonly workflowEntityService: WorkflowEntityService,
        private readonly appErrorHandler: AppErrorHandler,
        private readonly translocoService: TranslocoService,
        private readonly alertsService: AlertsService,
        @Inject(MAT_DIALOG_DATA) private readonly values: ModuleContentWorkflowDialogValues,
    ) {}
    private readonly workflowCreated = new EventEmitter();

    ngOnInit(): void {
        this.approvalRequestType = this.values.approvalRequestType;
        this.parentModule = this.values.module;
        this.validatablePlan = this.values.validatablePlan;
        this.planData = this.values.planData;
        this.planStateObject = this.values.planStateObject;
        this.newestModuleContentApplication = this.validatablePlan.moduleContentApplications
            .slice()
            .sort((a, b) => new Date(a.metadata.lastModifiedOn).getTime() - new Date(b.metadata.lastModifiedOn).getTime())
            .findLast(app => app.approvedModuleId === this.parentModule.id);

        this.workflowEntityService
            .getById(this.workFlowId, true)
            .pipe(take(1), this.appErrorHandler.defaultErrorHandlerFd())
            .subscribe(workflow => {
                this.workflow = workflow as ModuleContentWorkflow;
                this.workflowDecision = workflow.decision as WorkflowDecisionWithRecommendation;
            });
    }

    close(): void {
        this.dialogService.close();
    }

    private get applicationState(): string {
        return this.newestModuleContentApplication ? this.newestModuleContentApplication.state : 'UNKNOWN';
    }

    get applicationType(): string {
        return this.approvalRequestType === 'REQUIRED' ? 'RequiredModuleContentWorkflow' : 'CustomModuleContentWorkflow';
    }

    isPendingApproval(): boolean {
        return this.applicationState === 'REQUESTED' || this.applicationState === 'IN_HANDLING';
    }

    isApproved(): boolean {
        return this.isAccepted() || this.isConditional();
    }

    isRejected(): boolean {
        return this.applicationState === 'REJECTED';
    }

    isAccepted(): boolean {
        return this.applicationState === 'ACCEPTED';
    }

    isConditional(): boolean {
        return this.applicationState === 'CONDITIONAL';
    }

    get workFlowId(): OtmId {
        return this.newestModuleContentApplication?.id;
    }

    get selectedCourseUnits(): CourseUnit[] {
        return this.validatablePlan
            .getSelectedCourseUnitsUnderModule(this.parentModule)
            .filter(
                courseUnit => !_.get(this.planStateObject?.courseUnits, courseUnit.id)?.isInvalidAccordingToModuleContentApprovalRequested,
            );
    }

    get selectedCustomStudyDrafts(): CustomStudyDraft[] {
        return this.validatablePlan.getSelectedCustomStudyDraftsByParentModuleId(this.parentModule.id);
    }

    getCustomCourseUnitAttainment(id: OtmId): CustomCourseUnitAttainment {
        return this.validatablePlan.getCustomCourseUnitAttainment(id);
    }

    castToModuleContentWorkflowDecision(decision: WorkflowDecision): ModuleContentWorkflowDecision {
        return decision as ModuleContentWorkflowDecision;
    }

    castToWorkflowTypeEnumValue(value: string): WorkflowTypeEnumValue {
        return value as WorkflowTypeEnumValue;
    }

    cancelApplication(): void {
        if (this.workflow.cancellingDisabled) {
            this.showApplicationCancellationDisabledAlert = true;
            return;
        }
        this.confirmDialogFdOpener({
            title: this.translocoService.translate(`SHOW_MODULE_CONTENT_APPROVAL_MODAL.${this.applicationType}.CANCEL_TITLE`),
            description: this.translocoService.translate(`SHOW_MODULE_CONTENT_APPROVAL_MODAL.${this.applicationType}.CANCEL_CONTENT`),
            confirmObservable: of(true),
        } as ConfirmDialogFdValues<boolean>)
            .afterClosed()
            .pipe(
                take(1),
                switchMap((confirmed: boolean) => (confirmed ? this.workflowEntityService.cancel(this.workflow.id) : EMPTY)),
                this.appErrorHandler.defaultErrorHandlerFd(),
            )
            .subscribe((workflow: Workflow) => {
                if (workflow) {
                    this.workflowCreated.next(workflow);
                    this.dialogService.close(workflow);
                }
            });
    }
    revokeApplication(): void {
        if (this.workflow.cancellingDisabled) {
            this.showApplicationRevocationDisabledAlert = true;
            return;
        }
        this.confirmDialogFdOpener({
            title: this.translocoService.translate(`SHOW_MODULE_CONTENT_APPROVAL_MODAL.${this.applicationType}.REVOKE_TITLE`),
            description: this.translocoService.translate(`SHOW_MODULE_CONTENT_APPROVAL_MODAL.${this.applicationType}.REVOKE_CONTENT`),
            confirmObservable: of(true),
        } as ConfirmDialogFdValues<boolean>)
            .afterClosed()
            .pipe(
                take(1),
                switchMap((confirmed: boolean) => (confirmed ? this.workflowEntityService.revoke(this.workflow.id) : EMPTY)),
                this.appErrorHandler.defaultErrorHandlerFd(),
            )
            .subscribe((workflow: Workflow) => {
                if (workflow) {
                    this.workflowCreated.next(workflow);
                    this.dialogService.close(workflow);
                }
            });
    }

    private isAcceptedOrConditionalModuleContentWorkflowDecision(decision: WorkflowDecision): decision is ModuleContentWorkflowDecision {
        return (decision.state === 'ACCEPTED' || decision.state === 'CONDITIONAL') && decision.type === 'ModuleContentWorkflowDecision';
    }
}
